import React, { useState, useRef } from 'react';
import { TableBlock, TableCell } from '../../../core/ast/document.types';
import { Plus, Minus, Table as TableIcon, Upload, FileSpreadsheet } from 'lucide-react';
import { TableImportModal } from './TableImportModal';
import { parseExcelOrCsvFile } from '../../../core/ast/tableParser';

interface TableBlockEditorProps {
  block: TableBlock;
  onUpdate: (updates: Partial<TableBlock['data']>) => void;
}

export const TableBlockEditor: React.FC<TableBlockEditorProps> = ({ block, onUpdate }) => {
  const { cells, hasHeaderRow, caption, borderStyle = 'academic' } = block.data;
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const quickFileInputRef = useRef<HTMLInputElement>(null);

  const updateCell = (rowIndex: number, colIndex: number, newContent: string) => {
    const newCells: TableCell[][] = cells.map((row, r) =>
      row.map((cell, c) => {
        if (r === rowIndex && c === colIndex) {
          return { ...cell, content: newContent };
        }
        return cell;
      })
    );
    onUpdate({ cells: newCells });
  };

  /**
   * Smart Direct Paste Handler:
   * When pasting multi-cell / tab-delimited text into any cell,
   * paste the matrix starting from that cell (or expand table).
   */
  const handleCellPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    startRow: number,
    startCol: number
  ) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!clipboardData) return;

    // Check if it's multi-cell data (contains tab or newline)
    if (clipboardData.includes('\t') || clipboardData.includes('\n')) {
      e.preventDefault();

      const lines = clipboardData.trim().split(/\r?\n/);
      const pasteMatrix = lines.map((line) => line.split('\t'));

      const pasteRowCount = pasteMatrix.length;
      const pasteColCount = Math.max(...pasteMatrix.map((r) => r.length), 1);

      // Determine required rows and cols
      const targetRowCount = Math.max(cells.length, startRow + pasteRowCount);
      const currentCols = cells[0]?.length || 2;
      const targetColCount = Math.max(currentCols, startCol + pasteColCount);

      const now = Date.now();
      const newCells: TableCell[][] = [];

      for (let r = 0; r < targetRowCount; r++) {
        const row: TableCell[] = [];
        for (let c = 0; c < targetColCount; c++) {
          const isHeader = r === 0 && hasHeaderRow;
          const existingCell = cells[r]?.[c];

          // Check if this coordinate is within the paste area
          const pasteR = r - startRow;
          const pasteC = c - startCol;

          if (
            pasteR >= 0 &&
            pasteR < pasteRowCount &&
            pasteC >= 0 &&
            pasteC < (pasteMatrix[pasteR]?.length || 0)
          ) {
            const pastedValue = pasteMatrix[pasteR][pasteC].trim();
            row.push({
              id: existingCell?.id || `c_${r}_${c}_${now}_${Math.random().toString(36).substring(2, 6)}`,
              content: pastedValue,
              isHeader,
              bold: isHeader,
              alignment: isHeader ? 'center' : (isNaN(Number(pastedValue)) || pastedValue === '' ? 'left' : 'right'),
            });
          } else if (existingCell) {
            row.push(existingCell);
          } else {
            row.push({
              id: `c_${r}_${c}_${now}_${Math.random().toString(36).substring(2, 6)}`,
              content: '',
              isHeader,
              bold: isHeader,
              alignment: isHeader ? 'center' : 'left',
            });
          }
        }
        newCells.push(row);
      }

      onUpdate({
        rows: targetRowCount,
        cols: targetColCount,
        cells: newCells,
      });
    }
  };

  const handleQuickUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await parseExcelOrCsvFile(file);
      onUpdate(result.defaultTable);
    } catch (err: any) {
      alert(`Failed to import spreadsheet: ${err?.message || 'Invalid format'}`);
    } finally {
      if (quickFileInputRef.current) quickFileInputRef.current.value = '';
    }
  };

  const addRow = () => {
    const colCount = cells[0]?.length || 2;
    const newRow: TableCell[] = Array.from({ length: colCount }, (_, c) => ({
      id: `c${cells.length}_${c}_${Date.now()}`,
      content: '',
      alignment: 'center',
    }));
    onUpdate({
      rows: cells.length + 1,
      cells: [...cells, newRow],
    });
  };

  const removeRow = () => {
    if (cells.length <= 1) return;
    onUpdate({
      rows: cells.length - 1,
      cells: cells.slice(0, cells.length - 1),
    });
  };

  const addColumn = () => {
    const newCells = cells.map((row, r) => [
      ...row,
      {
        id: `c${r}_${row.length}_${Date.now()}`,
        content: r === 0 && hasHeaderRow ? `Header ${row.length + 1}` : '',
        isHeader: r === 0 && hasHeaderRow,
        alignment: 'center' as const,
      },
    ]);
    onUpdate({
      cols: (cells[0]?.length || 0) + 1,
      cells: newCells,
    });
  };

  const removeColumn = () => {
    if ((cells[0]?.length || 0) <= 1) return;
    const newCells = cells.map((row) => row.slice(0, row.length - 1));
    onUpdate({
      cols: cells[0].length - 1,
      cells: newCells,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-3)', backgroundColor: 'var(--bg-surface-subtle)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      {/* Table Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
          <TableIcon size={14} color="var(--primary-600)" />
          <span>Data Table ({cells.length} × {cells[0]?.length || 0})</span>
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Import / Paste Modal Trigger */}
          <button
            type="button"
            className="btn btn-primary btn-sm text-xs"
            onClick={() => setIsImportModalOpen(true)}
            title="Import Excel / Google Sheets or Paste tabular data"
          >
            <FileSpreadsheet size={13} /> Import / Paste Data
          </button>

          {/* Quick Upload Button */}
          <button
            type="button"
            className="btn btn-secondary btn-sm text-xs"
            onClick={() => quickFileInputRef.current?.click()}
            title="Upload .xlsx, .xls, .csv file"
          >
            <Upload size={13} /> Excel/CSV
          </button>
          <input
            ref={quickFileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,.tsv,.txt"
            style={{ display: 'none' }}
            onChange={handleQuickUpload}
          />

          <span style={{ color: 'var(--border-default)' }}>|</span>

          <button type="button" className="btn btn-secondary btn-sm text-xs" onClick={addRow} title="Add Row">
            <Plus size={12} /> Row
          </button>
          <button type="button" className="btn btn-secondary btn-sm text-xs" onClick={removeRow} title="Remove Row">
            <Minus size={12} /> Row
          </button>
          <button type="button" className="btn btn-secondary btn-sm text-xs" onClick={addColumn} title="Add Column">
            <Plus size={12} /> Col
          </button>
          <button type="button" className="btn btn-secondary btn-sm text-xs" onClick={removeColumn} title="Remove Column">
            <Minus size={12} /> Col
          </button>
        </div>
      </div>

      {/* Direct Paste Tip */}
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
        💡 <span>Tip: You can paste (Ctrl+V) tabular cells directly into any input box below to populate the grid automatically.</span>
      </div>

      {/* Grid Inputs */}
      <div style={{ overflowX: 'auto', maxHeight: '260px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {cells.map((row, rIdx) => (
              <tr key={rIdx}>
                {row.map((cell, cIdx) => (
                  <td key={cell.id || cIdx} style={{ padding: '2px', border: '1px solid var(--border-subtle)' }}>
                    <input
                      type="text"
                      className="form-input text-xs"
                      style={{
                        padding: '4px 6px',
                        fontWeight: rIdx === 0 && hasHeaderRow ? 700 : 'normal',
                        backgroundColor: rIdx === 0 && hasHeaderRow ? 'var(--bg-surface)' : undefined,
                        textAlign: cell.alignment || (rIdx === 0 && hasHeaderRow ? 'center' : 'left'),
                      }}
                      value={cell.content}
                      onChange={(e) => updateCell(rIdx, cIdx, e.target.value)}
                      onPaste={(e) => handleCellPaste(e, rIdx, cIdx)}
                      placeholder={rIdx === 0 && hasHeaderRow ? `Head ${cIdx + 1}` : '...'}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Settings (Caption & Border Style) */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-2)' }}>
        <div className="form-group">
          <label className="form-label">Table Caption</label>
          <input
            type="text"
            className="form-input text-xs"
            value={caption || ''}
            onChange={(e) => onUpdate({ caption: e.target.value })}
            placeholder="e.g. Table 1: Measured Diode Parameters"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Border Style</label>
          <select
            className="form-select text-xs"
            value={borderStyle}
            onChange={(e) => onUpdate({ borderStyle: e.target.value as any })}
          >
            <option value="academic">Academic (APA standard)</option>
            <option value="grid">Full Grid</option>
            <option value="minimal">Minimal</option>
          </select>
        </div>
      </div>

      {/* Table Import Modal */}
      <TableImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={(tableData) => onUpdate(tableData)}
      />
    </div>
  );
};
